/**
 * Exact money and quantity arithmetic.
 *  - Money is integer minor units (cents) with a currency code.
 *  - Quantities are integer thousandths ("milli-units"): 1.250 kg = 1250, 3 chairs = 3000.
 * No floating point is ever used for a stored value.
 */

export const CURRENCIES = {
  USD: { code: 'USD', name: 'US Dollar', symbol: 'US$', decimals: 2 },
  ZWG: { code: 'ZWG', name: 'Zimbabwe Gold (ZiG)', symbol: 'ZiG', decimals: 2 },
  ZAR: { code: 'ZAR', name: 'South African Rand', symbol: 'R', decimals: 2 },
  BWP: { code: 'BWP', name: 'Botswana Pula', symbol: 'P', decimals: 2 },
  GBP: { code: 'GBP', name: 'British Pound', symbol: '£', decimals: 2 },
  EUR: { code: 'EUR', name: 'Euro', symbol: '€', decimals: 2 },
} as const;

export type CurrencyCode = keyof typeof CURRENCIES;
export const CURRENCY_CODES = Object.keys(CURRENCIES) as CurrencyCode[];

export function isCurrencyCode(v: string): v is CurrencyCode {
  return v in CURRENCIES;
}

/** Formats integer cents, e.g. formatMoney(123456, 'USD') → "US$1,234.56". */
export function formatMoney(cents: number, currency: CurrencyCode, opts: { symbol?: boolean } = {}): string {
  assertSafeInteger(cents, 'cents');
  const { decimals, symbol } = CURRENCIES[currency];
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const factor = 10 ** decimals;
  const whole = Math.floor(abs / factor);
  const frac = abs % factor;
  const wholeStr = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = decimals > 0 ? `${wholeStr}.${frac.toString().padStart(decimals, '0')}` : wholeStr;
  const withSymbol = opts.symbol === false ? body : `${symbol}${symbol.length > 1 ? ' ' : ''}${body}`;
  return negative ? `-${withSymbol}` : withSymbol;
}

/**
 * Parses a user-entered amount ("1,234.5", "12", "0.07") into integer cents.
 * Returns null for anything that is not a clean decimal number or has too many decimals.
 */
export function parseMoney(input: string, currency: CurrencyCode = 'USD'): number | null {
  const decimals = CURRENCIES[currency].decimals;
  const s = input.trim().replace(/,/g, '');
  const re = new RegExp(`^(-)?(\\d+)(?:\\.(\\d{0,${decimals}}))?$`);
  const m = re.exec(s);
  if (!m) return null;
  const whole = Number(m[2]);
  const frac = Number((m[3] ?? '').padEnd(decimals, '0') || '0');
  const cents = whole * 10 ** decimals + frac;
  if (!Number.isSafeInteger(cents)) return null;
  return m[1] ? -cents : cents;
}

/** Integer division rounding half away from zero (standard retail rounding). */
export function divRound(numerator: number, denominator: number): number {
  if (denominator === 0) throw new Error('Division by zero');
  const sign = Math.sign(numerator) * Math.sign(denominator);
  const n = Math.abs(numerator);
  const d = Math.abs(denominator);
  const q = Math.floor(n / d);
  const r = n - q * d;
  return sign * (r * 2 >= d ? q + 1 : q);
}

/**
 * a × b ÷ c rounded half away from zero, computed with BigInt so intermediate products
 * (e.g. stock value × quantity) can never lose precision.
 */
export function mulDiv(a: number, b: number, c: number): number {
  assertSafeInteger(a, 'a');
  assertSafeInteger(b, 'b');
  assertSafeInteger(c, 'c');
  if (c === 0) throw new Error('Division by zero');
  const num = BigInt(a) * BigInt(b);
  const den = BigInt(c);
  const neg = num < 0n !== den < 0n && num !== 0n;
  const n = num < 0n ? -num : num;
  const d = den < 0n ? -den : den;
  let q = n / d;
  if ((n % d) * 2n >= d) q += 1n;
  const out = Number(neg ? -q : q);
  assertSafeInteger(out, 'result');
  return out;
}

/** Line total: unit price (cents per whole unit) × quantity (milli-units), rounded to the cent. */
export function lineTotal(unitPriceCents: number, qtyMilli: number): number {
  return mulDiv(unitPriceCents, qtyMilli, 1000);
}

/** Applies a percentage expressed in basis points (1550 = 15.50%). */
export function percentOf(cents: number, basisPoints: number): number {
  return mulDiv(cents, basisPoints, 10_000);
}

/** VAT contained in a VAT-inclusive amount: amount × rate / (1 + rate). */
export function vatFromInclusive(inclusiveCents: number, rateBasisPoints: number): number {
  return mulDiv(inclusiveCents, rateBasisPoints, 10_000 + rateBasisPoints);
}

/** Splits an amount into parts proportional to weights that sum exactly to the original (largest remainder). */
export function allocate(cents: number, weights: number[]): number[] {
  if (!weights.length) return [];
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) throw new Error('Weights must sum to a positive number');
  const sign = cents < 0 ? -1 : 1;
  const amount = BigInt(Math.abs(cents));
  const T = BigInt(total);
  const parts = weights.map((w) => (amount * BigInt(w)) / T);
  const rems = weights.map((w, i) => ({ i, r: (amount * BigInt(w)) % T }));
  let remainder = amount - parts.reduce((a, b) => a + b, 0n);
  rems.sort((x, y) => (y.r > x.r ? 1 : y.r < x.r ? -1 : x.i - y.i));
  for (let k = 0; remainder > 0n; k++, remainder--) parts[rems[k % rems.length]!.i]! += 1n;
  return parts.map((p) => sign * Number(p));
}

/** Formats milli-units for display: 1250 → "1.25", 3000 → "3". */
export function formatQty(milli: number, maxDecimals = 3): string {
  assertSafeInteger(milli, 'milli');
  const negative = milli < 0;
  const abs = Math.abs(milli);
  const whole = Math.floor(abs / 1000);
  const frac = (abs % 1000).toString().padStart(3, '0').slice(0, maxDecimals).replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${frac ? `.${frac}` : ''}`;
}

/** Parses "1.25" → 1250 milli-units. Returns null if invalid. */
export function parseQty(input: string): number | null {
  const m = /^(-)?(\d+)(?:\.(\d{0,3}))?$/.exec(input.trim());
  if (!m) return null;
  const v = Number(m[2]) * 1000 + Number((m[3] ?? '').padEnd(3, '0') || '0');
  if (!Number.isSafeInteger(v)) return null;
  return m[1] ? -v : v;
}

function assertSafeInteger(v: number, name: string) {
  if (!Number.isSafeInteger(v)) throw new Error(`${name} must be a safe integer, got ${v}`);
}
